import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IonSkeletonText, IonAlert } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { useToast } from 'contexts/ToastContext';
import billingService from 'services/billing';
import { PLANS, formatPrice } from 'constants/plans';
import PaymentModal from 'components/organisms/Modals/PaymentModal';
import CouponModal from 'components/organisms/Modals/CouponModal';
import AuthService from '../../services/auth';
import './BillingPage.css';

const BillingPage = () => {
  const [, setUser] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const autoCheckoutTriggered = useRef(false);

  const loadBillingData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingService.getBillingInfo();
      setBillingData(data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStripeSuccess = useCallback(async (sessionId) => {
    try {
      setLoading(true);
      const response = await billingService.processStripeSuccess(sessionId);
      
      if (response.success) {
        window.history.replaceState({}, document.title, '/billing');
        await loadBillingData();
        console.log('✅ Subscription activated successfully!');
      }
    } catch (error) {
      console.error('❌ Error processing Stripe success:', error);
    } finally {
      setLoading(false);
    }
  }, [loadBillingData]);

  const handlePaymentSuccess = (paymentIntent) => {
    console.log('Payment successful:', paymentIntent);
    loadBillingData();
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const successParam = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    const planParam = urlParams.get('plan');
    
    if (successParam === 'true' && sessionId) {
      handleStripeSuccess(sessionId);
    } else if (planParam && !autoCheckoutTriggered.current) {
      autoCheckoutTriggered.current = true;
      (async () => {
        try {
          const checkoutUrl = await billingService.createCheckoutSession(planParam);
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
            return;
          }
        } catch (err) {
          console.error('Auto-checkout failed, showing billing page:', err);
        }
        loadBillingData();
      })();
    } else {
      loadBillingData();
    }
  }, [handleStripeSuccess, loadBillingData]);

  const subscription = billingData ? billingService.formatSubscription(billingData.subscription) : null;
  const currentPlan = subscription?.plan || PLANS[0]; // Default to trial
  const usage = billingData ? billingService.formatUsage(billingData.usage, currentPlan) : null;

  // Get user info
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Billing - Zibby</title>
        <meta name="description" content="Manage your Zibby subscription and billing information." />
      </Helmet>
      <div className="billing-page page-fade-in main-body page-layout">
        <div className="page-header-center">
          <h1>Billing & Invoices</h1>
          <p>Manage your subscription and billing information</p>
        </div>
      
        <BillingPageContent 
          billingData={billingData}
          loading={loading}
          setLoading={setLoading}
          showPaymentModal={showPaymentModal}
          setShowPaymentModal={setShowPaymentModal}
          loadBillingData={loadBillingData}
          handlePaymentSuccess={handlePaymentSuccess}
          subscription={subscription}
          currentPlan={currentPlan}
          usage={usage}
        />
      </div>
    </>
  );
};

const BillingPageContent = ({ 
  billingData, 
  loading,
  setLoading,
  showPaymentModal, 
  setShowPaymentModal, 
  loadBillingData,
  handlePaymentSuccess,
  subscription,
  currentPlan,
  usage
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [showCancelAlert, setShowCancelAlert] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);

  const handleChangePlan = async (planId) => {
    try {
      // Check if it's a free plan
      const plan = PLANS.find(p => p.id === planId);
      if (plan && plan.price === 0) {
        // Handle free plan activation
        await billingService.activateTrialPlan();
        await loadBillingData();
        showToast('Trial plan activated successfully!', 'success');
        return;
      }

      // Handle paid plans
      const checkoutUrl = await billingService.createCheckoutSession(planId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        showToast('Failed to create checkout session', 'error');
      }
    } catch (error) {
      console.error('Error changing plan:', error);
      showToast('Failed to change plan, please try again.', 'error');
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelAlert(true);
  };

  const confirmCancelSubscription = async () => {
    try {
      const result = await billingService.cancelSubscription(true);
      
      // Use the corrected period end from Stripe (returned by backend)
      const endDate = result?.currentPeriodEnd
        ? new Date(result.currentPeriodEnd).toLocaleDateString()
        : subscription?.currentPeriodEnd 
          ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
          : 'the end of your billing period';
      
      showToast(`Subscription cancelled. Access continues until ${endDate}`, 'success');
      loadBillingData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showToast('Failed to cancel subscription', 'error');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setLoading(true);
      await billingService.reactivateSubscription();
      showToast('Subscription reactivated successfully!', 'success');
      loadBillingData();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      showToast('Failed to reactivate subscription', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="page-card-content">
        {/* Current Plan */}
        <div className="page-card">
          <div className="card-header">
            <h2>Current Plan</h2>
            {loading ? (
              <IonSkeletonText animated style={{ width: '60px', height: '20px' }} />
            ) : (
              <div className={`status-badge status-${subscription?.cancelAtPeriodEnd ? 'cancelled' : (subscription?.status || 'active')}`}>
                {subscription?.cancelAtPeriodEnd 
                  ? `Cancelled - Active until ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                  : subscription?.status === 'active' ? 'Active' : subscription?.status || 'Active'}
              </div>
            )}
          </div>
          
          <div className="plan-details">
            <div className="plan-info">
              {loading ? (
                <>
                  <IonSkeletonText animated style={{ width: '120px', height: '24px', marginBottom: '8px' }} />
                  <IonSkeletonText animated style={{ width: '80px', height: '20px', marginBottom: '8px' }} />
                  <IonSkeletonText animated style={{ width: '200px', height: '16px' }} />
                </>
              ) : (
                <>
                  <h3>{currentPlan.name}</h3>
                  <p className="plan-price">{formatPrice(currentPlan.price)}<span>{currentPlan.price > 0 ? '/month' : ''}</span></p>
                  <p className="plan-description">
                    {currentPlan.features.join(', ')}
                  </p>
                </>
              )}
            </div>
              
            <div className="billing-details">
              {subscription?.nextBillingDate && (
                <div className="billing-detail">
                  <span className="detail-label">Next billing date:</span>
                  <span className="detail-value">{subscription.nextBillingDate.toLocaleDateString()}</span>
                </div>
              )}
              {billingData?.paymentMethod && (
                <div className="billing-detail">
                  <span className="detail-label">Payment method:</span>
                  <span className="detail-value">**** **** **** {billingData.paymentMethod.last4}</span>
                </div>
              )}
            </div>
          </div>

          {!loading && (
            <div className="plan-actions">
              {/* Show upgrade to Pro if on free plan or subscription cancelled */}
              {(!subscription?.id || subscription?.status !== 'active' || subscription?.cancelAtPeriodEnd) &&
                currentPlan?.id !== 'pro' && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleChangePlan('pro')}
                  >
                    Upgrade to Pro - {formatPrice(49)}/mo
                  </button>
                  <span
                    className="enterprise-link"
                    onClick={() => setShowCouponModal(true)}
                    style={{ marginLeft: 12 }}
                  >
                    Use coupon
                  </span>
                </>
              )}
              
              {/* Show cancel button only if user has an active paid subscription that's not cancelled */}
              {subscription?.id && subscription?.status === 'active' && !subscription?.cancelAtPeriodEnd && (
                <button 
                  className="btn btn-danger"
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </button>
              )}
              
              {/* Show reactivate button for cancelled subscriptions */}
              {subscription?.id && subscription?.status === 'active' && subscription?.cancelAtPeriodEnd && (
                <button 
                  className="btn btn-light"
                  onClick={handleReactivateSubscription}
                  disabled={loading}
                >
                  Reactivate Subscription
                </button>
              )}

              {/* Enterprise link */}
              <span 
                className="enterprise-link"
                onClick={() => navigate('/contact', { state: { prefill: 'enterprise' } })}
              >
                Looking for Enterprise?
              </span>
            </div>
          )}
        </div>

        {/* Invoice History */}
        <div className="page-card">
          <div className="card-header">
            <h2>Invoice History</h2>
            <button className="btn">Download All</button>
          </div>
          
          <div className="invoices-table">
            <div className="billing-table-header">
              <span>Invoice ID</span>
              <span>Date</span>
              <span>Description</span>
              <span>Amount</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            
            {billingData?.invoices?.length > 0 ? (
              billingData.invoices.map((invoice) => (
                <div key={invoice.id} className="billing-table-row">
                  <span className="invoice-id">{invoice.id}</span>
                  <span>{new Date(invoice.date).toLocaleDateString()}</span>
                  <span>{invoice.description}</span>
                  <span className="amount">{formatPrice(invoice.amount)}</span>
                  <span className={`status-badge status-${invoice.status.toLowerCase()}`}>
                    {invoice.status}
                  </span>
                  <div className="row-actions">
                    {invoice.downloadUrl ? (
                      <a
                        className="btn-link"
                        href={invoice.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Download
                      </a>
                    ) : (
                      <span className="btn-link disabled">—</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-invoices">
                <p>No invoices available</p>
              </div>
            )}
          </div>
        </div>
      </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handlePaymentSuccess}
          planDetails={{
            plan: currentPlan?.name || 'Loading...',
            amount: currentPlan?.price || 0,
            description: currentPlan?.features?.join(', ') || 'Loading...'
          }}
          />
        )}

        {/* Coupon Modal */}
        <CouponModal
          isOpen={showCouponModal}
          onDismiss={() => setShowCouponModal(false)}
          onApplied={(res) => {
            const msg = res?.discount
              ? `Coupon applied — ${res.discount}`
              : 'Coupon applied';
            showToast(msg, 'success');
            loadBillingData();
          }}
        />

        {/* Cancel Subscription Alert */}
        <IonAlert
          isOpen={showCancelAlert}
          onDidDismiss={() => setShowCancelAlert(false)}
          header="Cancel Subscription"
          message="Are you sure you want to cancel your subscription? It will remain active until the end of your billing period."
          buttons={[
            {
              text: 'Keep Subscription',
              role: 'cancel',
              cssClass: 'secondary'
            },
            {
              text: 'Cancel Subscription',
              cssClass: 'danger',
              handler: confirmCancelSubscription
            }
          ]}
        />
    </>
  );
};

export default BillingPage;
